import React, { useState, useEffect, useRef } from "react";
import { Card, Label, Input, TextArea, Button } from "./components/UI.jsx";
import IntakeCompleteModal from "./components/IntakeCompleteModal.jsx";


const API_BASE = process.env.REACT_APP_COMPANY_API || "http://127.0.0.1:8001";

const REVISION_SECTION_LABELS = {
  IDENTITY: "Identiteit",
  AMBITION: "Ambitie",
  SKILLS: "Vacatures & vaardigheden",
  CONDITIONS: "Voorwaarden",
};

const ChatMessage = ({ role, text }) => { 
  const isUser = role === "user";
  
  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} mb-6 animate-fade-in-up`}>
      <div 
        className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm
          ${isUser 
            ? "bg-brand-purple text-white rounded-tr-none" 
            : "bg-white border border-gray-200 text-gray-800 rounded-tl-none"
          }`}
      >
        {text}
      </div>
    </div>
  );
};

// --- MAIN APP COMPONENT ---

export default function CompanyApp({ userId, initialPersonalia, apiBase, onBackToDashboard, onBackToHome }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'werepresenttalent.nl - Bedrijf intake';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const [step, setStep] = useState(1);
  const [activeRevision, setActiveRevision] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [loadingState, setLoadingState] = useState(true); // Voor hydration
  const companyId = userId; // Gebruik het ID van registratie, niet een random ID
  const [busy, setBusy] = useState(false);

  // PERSONALIA WORDT NU BIJ REGISTRATIE GEDAAN - DEZE STAP IS OVERGESLAGEN
  // const [personalia, setPersonalia] = useState({
  //   name: "",
  //   email: "",
  //   phone: "",
  //   sector: "",
  //   one_liner_description: "",
  //   function: "",
  //   url: ""
  // });

  // Stap 2: Identiteitsprofiel (Search Result + Edit)
  const [digitalTwin, setDigitalTwin] = useState({
    reason_of_existence: "",
    long_term_goals: "",
    unique_selling_points: "",
    critical_roles_and_skills: "",
    teams_and_collaboration: "",
    extra_explanation: "" // Extra veld voor de gebruiker
  });
  const [searchSources, setSearchSources] = useState([]);

  // Stap 3: Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Stap 4: Rapport
  // ... bestaande states ...
  const [ambitionText, setAmbitionText] = useState("");

  const [vacancies, setVacancies] = useState([]);
  const [noVacanciesFoundOnline, setNoVacanciesFoundOnline] = useState(false);
  const [termsContent, setTermsContent] = useState(""); // De geparsed tekst
  const [opportunities, setOpportunities] = useState(null); // De match resultaten
  const [conditionsMode, setConditionsMode] = useState("upload"); // upload | manual
  const [constraintOptions, setConstraintOptions] = useState([]);
  const [manualConstraints, setManualConstraints] = useState({});
  const [loadingConstraintOptions, setLoadingConstraintOptions] = useState(false);
  const hasAutoStartedAmbitionRevision = useRef(false);

  // === HYDRATION EFFECT ===
  useEffect(() => {
    async function restoreSession() {
      try {
        const res = await fetch(`${apiBase}/api/company/status?companyId=${encodeURIComponent(userId)}`);
        const status = await res.json();
        
        if (!status.exists) {
            // Geen sessie, start bij identiteitsprofiel-search (personalia al bekend)
            setStep(1); // Toon welkomst/start scherm
        } else {
            // == HERSTEL DE STATUS ==
            setActiveRevision(status.revision || null);

          const identityProfile = status.search_context?.identity_profile;
          if (identityProfile) {
            setDigitalTwin(prev => ({
              ...prev,
              ...identityProfile,
            }));
          }
          if (status.ambition) {
            setAmbitionText(status.ambition);
          }
            
            // 1. History inladen (voor het interview)
            if (status.history && status.history.length > 0) {
                setChatHistory(status.history);
            }

            // 2. Routering naar juiste stap op basis van phase (zoals bij CandidateApp)
            const phase = status.phase;
            
            if (phase === "SEARCH") {
                if (status.needs_search_confirm) {
                    // Identiteitsprofiel moet bevestigd worden
                    setStep(2);
                } else {
                    setStep(1); // Start search nog niet gedaan
                }
            }
            else if (phase === "INTERVIEW") {
                if (status.needs_ambition_review) {
                    // Interview klaar, wachten op confirm
                    setStep(4); // Ambition review / vacature start
                } else {
                    setStep(3); // Chat scherm
                }
            }
            else if (phase === "VACANCIES") {
                if (status.needs_vacancy_review) {
                    // Vacatures moeten bevestigd
                    setStep(5);
                } else {
                    setStep(4); // Vacature search starten
                }
            }
            else if (phase === "CONDITIONS") {
                // Conditions fase
                setStep(6); // Voorwaarden upload
            }
            else if (phase === "COMPLETED") {
                // Afgerond
                setStep(7); // Results scherm
                setShowCompletionModal(true);
            }
            else {
                // Fallback
                setStep(1);
            }
        }
      } catch (e) {
        console.error("Restore error:", e);
        setStep(1); // Fallback naar start
      } finally {
        setLoadingState(false);
      }
    }
    restoreSession();
  }, []); // Run once on mount

  useEffect(() => {
    async function startAmbitionRevisionInterview() {
      if (loadingState) return;
      if (step !== 3) return;
      if (activeRevision?.section !== "AMBITION") return;
      if (chatHistory.length > 0) return;
      if (hasAutoStartedAmbitionRevision.current) return;

      hasAutoStartedAmbitionRevision.current = true;
      setBusy(true);
      try {
        const res = await fetch(`${API_BASE}/api/company/start-interview?companyId=${encodeURIComponent(companyId)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `HTTP ${res.status}`);
        }

        const intRes = await res.json();
        setChatHistory(intRes.history || [{ role: "model", text: intRes.message }]);
      } catch (e) {
        alert("Fout bij starten interview: " + e.message);
      } finally {
        setBusy(false);
      }
    }

    startAmbitionRevisionInterview();
  }, [activeRevision, chatHistory.length, companyId, loadingState, step]);

  useEffect(() => {
    async function fetchConstraintOptions() {
      if (step !== 6) return;

      setLoadingConstraintOptions(true);
      try {
        const res = await fetch(`${API_BASE}/api/company/constraints/options?companyId=${encodeURIComponent(companyId)}`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setConstraintOptions(data.options || []);
        setManualConstraints(data.selected_constraints || {});
      } catch (err) {
        console.error("Kon constraints-opties niet laden:", err);
      } finally {
        setLoadingConstraintOptions(false);
      }
    }

    fetchConstraintOptions();
  }, [step, companyId]);

  // Functie voor "Pauzeren / Terug naar Dashboard"
  const handlePause = () => {
    // State is in de backend al opgeslagen.
    if (onBackToDashboard) {
      onBackToDashboard();
    }
  };

  // --- API HELPERS ---
  async function postJSON(url, body) {
    const res = await fetch(
      API_BASE + url + `?companyId=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // --- HANDLERS ---

  // PERSONALIA STAP IS OVERGESLAGEN - Start direct bij identiteitsprofiel-search
  // Stap 1 (nu overgeslagen) -> Stap 2: Run Search
  async function startDigitalTwinSearch() {
    setBusy(true);
    try {
      // Trigger AI Search (personalia is al bekend uit registratie)
      const searchRes = await postJSON("/api/company/search", {});
      
      // Vul de identiteitsprofiel-state met de resultaten
      if (searchRes.ok && searchRes.data) {
        const dtData = searchRes.data.identity_profile;
        setDigitalTwin(prev => ({
            ...prev,
            ...dtData // Overschrijf lege velden met AI data
        }));
        setSearchSources(searchRes.data.sources_used || []);
        setStep(2);
      }

    } catch (e) {
      alert("Fout bij ophalen bedrijfsinformatie: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  // Stap 2 -> Stap 3: Bevestig identiteitsprofiel + Start Interview
  async function handleConfirmDigitalTwin() {
    setBusy(true);
    try {
      // 1. Stuur de (mogelijk aangepaste) twin terug
      console.log("Bevestigd identiteitsprofiel:", digitalTwin);
      await postJSON("/api/company/confirm", digitalTwin);

      if (activeRevision?.section === "IDENTITY") {
        finishRevision();
        return;
      }

      // 2. Start het interview
      const intRes = await postJSON("/api/company/start-interview", {});
      
      setChatHistory(intRes.history || [{ role: "model", text: intRes.message }]);
      setStep(3);

    } catch (e) {
      alert("Fout bij starten interview: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  // Stap 3: Chat Loop
  async function handleChatSend(msg = null) {
    const text = msg || userInput.trim();
    if (!text) return;

    // UI update
    setChatHistory(prev => [...prev, { role: "user", text }]);
    setUserInput("");
    setIsTyping(true);

    try {
      const res = await postJSON("/api/company/run-interview", { message: text });
      
      if (res.ok) {
        if (res.history) {
            setChatHistory(res.history);
        } else if (res.message) {
            setChatHistory(prev => [...prev, { role: "model", text: res.message }]);
        }
        if (res.done) {
            console.log("Interview afgerond. Verificatie door user.");
            setAmbitionText(res.ambition || "");
            setStep(4);
        }
      }
    } catch (e) {
      alert("Chat fout: " + e.message);
    } finally {
      setIsTyping(false);
    }
  }

            // setStep(4); 
  // --- EFFECTS ---
  async function handleRunVacancySearch() {
    setBusy(true);
    try {
        // Stuur de (aangepaste) string naar de backend
        const res = await postJSON(
            `/api/company/run-vacancy-search`, 
            ambitionText // De string body
        );

        if (res.done) {
            finishRevision();
            return;
        }

        const foundVacancies = res.vacancies?.vacancies || [];
        setVacancies(foundVacancies);
        setNoVacanciesFoundOnline(foundVacancies.length === 0);

        // NU pas door naar het vacature scherm (Stap 5)
        setStep(5);
    } catch (e) {
        alert("Fout bij zoeken vacatures: " + e.message);
    } finally {
        setBusy(false);
    }
  }

  // --- API HELPERS ---
  async function postJSON(url, body) {
    const res = await fetch(
      API_BASE + url + `?companyId=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // --- HANDLERS ---

  // Wijzig tekstvelden (Titel, Locatie, etc.)
  function handleVacancyChange(index, field, value) {
    const updated = [...vacancies];
    updated[index] = { ...updated[index], [field]: value };
    setVacancies(updated);
  }

  // Wijzig skills (speciale afhandeling voor komma-gescheiden string)
  function handleSkillChange(index, value) {
    const updated = [...vacancies];
    // We slaan de ruwe input even op in een tijdelijk veld '_skillString'
    // zodat het typen soepel gaat.
    updated[index] = { ...updated[index], _skillString: value }; 
    setVacancies(updated);
  }

  // Nieuwe lege vacature toevoegen
  function addVacancy() {
    setVacancies([
      ...vacancies, 
      { 
        job_title: "", 
        location: "", 
        employment_type: "", 
        job_description: "", 
        required_skills: [] 
      }
    ]);
  }

  // Vacature verwijderen
  function removeVacancy(index) {
    if (window.confirm("Weet je zeker dat je deze vacature wilt verwijderen?")) {
        setVacancies(vacancies.filter((_, i) => i !== index));
    }
  }

  // Opslaan en naar de backend sturen
  async function handleSubmitVacancies() {
    setBusy(true);
    try {
        // Data prepareren: Zorg dat skills weer netjes een Array worden
        const payloadVacancies = vacancies.map(v => {
            // Bepaal de skills: of uit de tijdelijke string, of uit de originele array
            let skillsArray = [];
            
            if (v._skillString !== undefined) {
                // Als de gebruiker heeft getypt: splitsen op komma
                skillsArray = v._skillString.split(",").map(s => s.trim()).filter(s => s);
            } else if (Array.isArray(v.required_skills)) {
                // Als er niet getypt is: gebruik het origineel
                skillsArray = v.required_skills;
            }

            return {
                ...v,
                required_skills: skillsArray
            };
        });

        const payload = {
            no_vacancies: payloadVacancies.length === 0,
            vacancies: payloadVacancies,
            sources_used: [] // Mag leeg zijn bij revisie
        };

        // Stuur naar het nieuwe endpoint
        await postJSON("/api/company/confirm-vacancies", payload);
        
        if (activeRevision?.section === "SKILLS") {
            finishRevision();
            return;
        }

        // Ga naar de eindstap
        setStep(6);

    } catch (e) {
        alert("Opslaan mislukt: " + e.message);
    } finally {
        setBusy(false);
    }
  }

  // --- TERMS & MATCHING HANDLERS ---

// Voeg deze toe aan je handlers
  async function handleConditionsUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    setBusy(true);
    const formData = new FormData();
    formData.append("file", file); // Key moet 'file' zijn zoals in backend parameter

    try {
        // Let op: GEEN 'Content-Type': 'application/json' header zetten!
        // De browser stelt automatisch de boundary in voor multipart/form-data.
        const res = await fetch(`${API_BASE}/api/company/conditions?companyId=${encodeURIComponent(companyId)}`, {
            method: "POST",
            body: formData
        });

        if (!res.ok) {
            const txt = await res.text();
            throw new Error(txt);
        }

        // Als het gelukt is, direct door naar de volgende stap (Matching/Resultaat)
        if (activeRevision) {
            finishRevision();
            return;
        }
        setStep(7); 
        setShowCompletionModal(true);

    } catch (err) {
        alert("Fout bij verwerken voorwaarden: " + err.message);
    } finally {
        setBusy(false);
    }
  }

  function setManualConstraintValue(key, offered) {
    setManualConstraints(prev => ({ ...prev, [key]: offered }));
  }

  async function handleSubmitManualConditions() {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/company/conditions/manual?companyId=${encodeURIComponent(companyId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selected_constraints: manualConstraints }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      if (activeRevision) {
        finishRevision();
        return;
      }

      setStep(7);
      setShowCompletionModal(true);
    } catch (err) {
      alert("Fout bij verwerken voorwaarden: " + err.message);
    } finally {
      setBusy(false);
    }
  }

  // Een herziening eindigt zodra het onderdeel opnieuw is afgerond.
  function finishRevision() {
    setActiveRevision(null);
    if (onBackToDashboard) onBackToDashboard();
  }

  async function handleCancelRevision() {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/api/company/revision/cancel?companyId=${encodeURIComponent(companyId)}`, { method: "POST" });
      finishRevision();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  }

  // 3. Trigger Matching & Haal Opportunities op
  async function handleGenerateOpportunities() {
    setBusy(true);
    try {
        // We roepen het specifieke endpoint aan dat je net noemde
        // Let op: companyId gaat mee als query parameter (?companyId=...)
        const res = await postJSON(`/api/company/opportunities`, {});
        
        // We gaan ervan uit dat de backend { opportunities: [...] } of direct een lijst teruggeeft
        // Pas dit eventueel aan op basis van je exacte backend return shape
        const matches = res.opportunities || res; 
        
        setOpportunities(matches);
    } catch (e) {
        alert("Fout bij ophalen matches: " + e.message);
    } finally {
        setBusy(false);
    }
  }

  // --- EFFECTS ---
  // Auto-scroll to bottom for chat messages
  useEffect(() => {
    if (step === 3) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory, isTyping, step]);

  // --- RENDER GUARDS ---
  if (loadingState) {
      return <div className="h-screen flex items-center justify-center text-[#3f7f65]">Je sessie wordt geladen...</div>;
  }

  // --- RENDER ---

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] pb-20 font-sans text-gray-800 selection:bg-green-100 selection:text-green-900">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />
      
      {/* Header Bar */}
      <header className="sticky top-0 z-10 border-b-4 border-[#151827] bg-white/95 shadow-sm backdrop-blur safe-top">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Top row: Logo + controls */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
               <img src="/logozondertekst.png" alt="werepresenttalent.nl" className="h-6 sm:h-8 w-auto flex-shrink-0" />
              <h1 className="truncate text-base font-black tracking-tight text-[#3f7f65] sm:text-xl">Bedrijfsintake</h1>
            </div>
            
            {/* PAUZE KNOP */}
            <button 
              onClick={handlePause}
              className="touch-target flex flex-shrink-0 items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#3f7f65] sm:text-sm"
            >
              <span className="hidden sm:inline">✖ Opslaan & Stoppen</span>
              <span className="sm:hidden">✖ Stop</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 py-10">

        {activeRevision && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[1.2rem] border-2 border-[#3f7f65] bg-[#eef6f2] px-4 py-3">
            <p className="text-sm font-bold text-[#151827]">
              Je doorloopt het onderdeel “{REVISION_SECTION_LABELS[activeRevision.section] || activeRevision.section}” opnieuw. Je huidige gegevens blijven staan tot je dit onderdeel afrondt.
            </p>
            <button
              onClick={handleCancelRevision}
              disabled={busy}
              className="rounded-lg border-2 border-[#151827] bg-white px-3 py-1.5 text-xs font-bold text-[#151827] transition hover:bg-gray-50 disabled:opacity-50"
            >
              Herziening annuleren
            </button>
          </div>
        )}

        {/* Progress Stepper - gebaseerd op fases */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-xs font-medium">
            {[
              { key: "SEARCH", step: 1, label: "Identiteit", icon: "🔍" },
              { key: "INTERVIEW", step: 3, label: "Ambities", icon: "💬" },
              { key: "VACANCIES", step: 4, label: "Vacatures", icon: "📋" },
              { key: "CONDITIONS", step: 6, label: "Voorwaarden", icon: "⚖️" },
              { key: "COMPLETED", step: 7, label: "Klaar", icon: "✓" },
            ].map((phase, idx, arr) => {
              const isActive = step >= phase.step && (idx === arr.length - 1 || step < arr[idx + 1].step);
              const isComplete = idx < arr.length - 1 && step >= arr[idx + 1].step;
              const isLast = idx === arr.length - 1;
              
              return (
                <React.Fragment key={phase.key}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm mb-1 transition-all
                      ${isComplete ? "bg-[#3f7f65] text-white" : isActive ? "bg-[#e9f4ef] text-[#3f7f65] ring-2 ring-[#3f7f65]" : "bg-gray-100 text-gray-400"}
                    `}>
                      {isComplete ? "✓" : phase.icon}
                    </div>
                    <span className={`hidden sm:block ${isActive ? "font-bold text-[#3f7f65]" : isComplete ? "text-[#3f7f65]" : "text-gray-400"}`}>
                      {phase.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div className={`mx-1 h-0.5 flex-1 ${isComplete ? "bg-[#3f7f65]" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Terug naar website link */}
        {onBackToHome && (
          <div className="mb-4 text-center">
            <button 
              onClick={onBackToHome}
              className="text-sm text-gray-400 hover:text-brand-purple transition-colors"
            >
              ← Terug naar website
            </button>
          </div>
        )}

        {/* STAP 1: BASIS INFO - NU UITGESCHAKELD (wordt bij registratie gedaan) */}
        {/* {step === 1 && (
          <div className="animate-fade-in-up space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900">Bedrijfsprofiel</h2>
              <p className="text-gray-500 mt-2">We scannen publieke bronnen om je identiteitsprofiel te starten.</p>
            </div>
            
            <Card className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Bedrijfsnaam</Label>
                    <Input placeholder="Bijv. Acme Corp" value={personalia.name} onChange={e => setPersonalia({...personalia, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Website URL</Label>
                    <Input placeholder="https://www.acme.com" value={personalia.url} onChange={e => setPersonalia({...personalia, url: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input placeholder="info@acme.com" value={personalia.email} onChange={e => setPersonalia({...personalia, email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Telefoonnummer</Label>
                    <Input placeholder="+31 6 12345678" value={personalia.phone} onChange={e => setPersonalia({...personalia, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Sector</Label>
                    <Input placeholder="Bijv. SaaS / FinTech" value={personalia.sector} onChange={e => setPersonalia({...personalia, sector: e.target.value})} />
                  </div>
                  <div>
                    <Label>Naam Contactpersoon</Label>
                    <Input placeholder="Jouw naam" value={personalia.function} onChange={e => setPersonalia({...personalia, function: e.target.value})} />
                  </div>
                </div>
                <div>
                   <Label>Korte omschrijving (One-liner)</Label>
                   <Input 
                     placeholder="Wat doet het bedrijf in één zin?" 
                     value={personalia.one_liner_description}
                     onChange={e => setPersonalia({...personalia, one_liner_description: e.target.value})}
                   />
                </div>

                <div className="pt-4 flex justify-end">
                  <Button onClick={handlePersonaliaSubmit} disabled={busy}>
                    {busy ? "Analyseren..." : "Start AI Scan"}
                  </Button>
                </div>
            </Card>
          </div>
        )} */}

        {/* WELKOMSTSCHERM (nieuwe step 1 - was step 2) */}
        {step === 1 && (
          <div className="animate-fade-in-up space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900">Welkom bij werepresenttalent.nl</h2>
              <p className="text-gray-500 mt-2">Laten we je bedrijfsprofiel verrijken met AI.</p>
            </div>
            <Card className="text-center p-10">
              <div className="space-y-6">
                <div className="text-6xl mb-4">🏢</div>
                <h3 className="text-2xl font-bold text-gray-900">Digitale analyse van je organisatie</h3>
                <p className="text-gray-600 max-w-xl mx-auto">
                  We scannen publieke bronnen om een rijk profiel van je organisatie op te bouwen.
                  Dit helpt ons de perfecte matches te vinden.
                </p>
                <div className="pt-4">
                  <Button onClick={startDigitalTwinSearch} disabled={busy}>
                    {busy ? "Analyseren..." : "Start AI Scan 🚀"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* STAP 2: IDENTITEITSPROFIEL EDIT (was stap 3) */}
        {step === 2 && (
           <div className="animate-fade-in-up space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-extrabold text-gray-900">Identiteitsprofiel (Concept)</h2>
                <p className="text-gray-500 mt-2">
                    De AI heeft deze informatie gevonden. Pas aan waar nodig, dit vormt de basis voor het interview.
                </p>
                {searchSources.length > 0 && (
                    <div className="mt-2 text-xs text-gray-400">
                        Bronnen: {searchSources.join(", ")}
                    </div>
                )}
              </div>

              <Card className="space-y-6 border-[#151827]/25 shadow-md">
                 <div className="space-y-4">
                    <div>
                        <Label>Waarom bestaan jullie? (Purpose)</Label>
                        <TextArea 
                            value={digitalTwin.reason_of_existence} 
                            onChange={e => setDigitalTwin({...digitalTwin, reason_of_existence: e.target.value})}
                        />
                    </div>
                    <div>
                        <Label>Lange termijn doelen</Label>
                        <TextArea 
                            value={digitalTwin.long_term_goals} 
                            onChange={e => setDigitalTwin({...digitalTwin, long_term_goals: e.target.value})}
                        />
                    </div>
                    <div>
                        <Label>Unieke kracht (USPs)</Label>
                        <TextArea 
                            value={digitalTwin.unique_selling_points} 
                            onChange={e => setDigitalTwin({...digitalTwin, unique_selling_points: e.target.value})}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Kritische Rollen & Skills</Label>
                            <TextArea 
                                className="min-h-[150px]"
                                value={digitalTwin.critical_roles_and_skills} 
                                onChange={e => setDigitalTwin({...digitalTwin, critical_roles_and_skills: e.target.value})}
                            />
                        </div>
                        <div>
                            <Label>Teams & Samenwerking</Label>
                            <TextArea 
                                className="min-h-[150px]"
                                value={digitalTwin.teams_and_collaboration} 
                                onChange={e => setDigitalTwin({...digitalTwin, teams_and_collaboration: e.target.value})}
                            />
                        </div>
                    </div>
                    
                    <div className="rounded-lg border border-[#151827]/20 bg-[#f6f1e8] p-4">
                        <Label>Extra Toelichting / Correcties</Label>
                        <p className="text-xs text-gray-500 mb-2">Mis je nog iets? Wil je een specifiek accent leggen?</p>
                        <TextArea 
                            placeholder="Typ hier extra context..."
                            value={digitalTwin.extra_explanation} 
                            onChange={e => setDigitalTwin({...digitalTwin, extra_explanation: e.target.value})}
                        />
                    </div>
                 </div>

                 <div className="pt-4 flex justify-end">
                    <Button onClick={handleConfirmDigitalTwin} disabled={busy}>
                      {busy
                        ? "Laden..."
                        : activeRevision?.section === "IDENTITY"
                          ? "Bevestig & rond af ➤"
                          : "Bevestig & Start Interview ➤"}
                    </Button>
                 </div>
              </Card>
           </div>
        )}

        {/* STAP 3: INTERVIEW */}
        {step === 3 && (
            <div className="flex flex-col h-[75vh] safe-bottom">
                <div className="scroll-touch scrollbar-hide no-overscroll mb-4 flex-1 space-y-4 overflow-y-auto rounded-xl border border-[#151827]/20 bg-white/90 p-4 shadow-inner">
                    {chatHistory.map((msg, i) => (
                        <ChatMessage key={i} role={msg.role} text={msg.text} />
                    ))}
                    
                    {isTyping && (
                        <div className="flex justify-start animate-pulse">
                            <div className="bg-white border border-gray-200 text-gray-400 text-xs px-4 py-2 rounded-2xl rounded-tl-none shadow-sm italic">
                                AI schrijft...
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <Card className="sticky bottom-2 !rounded-2xl border-t border-[#151827]/15 p-3 shadow-lg">
                    <div className="flex gap-2">
                        <textarea
                            className="h-14 flex-1 resize-none rounded-lg border-2 border-[#151827]/25 px-4 py-3 text-sm focus:border-[#3f7f65] focus:outline-none"
                            placeholder="Typ je antwoord..."
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleChatSend();
                                }
                            }}
                            disabled={isTyping}
                        />
                        <Button 
                            onClick={() => handleChatSend()} 
                            disabled={isTyping || !userInput.trim()} 
                            className="h-14 w-14 !p-0 rounded-full"
                        >
                            ➤
                        </Button>
                    </div>
                </Card>
            </div>
        )}

        {/* STAP 4: AMBITIE EDITEN */}
        {step === 4 && (
            <div className="animate-fade-in-up space-y-6">
                <div className="text-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900">
                {activeRevision?.section === "SKILLS" ? "Vacatures zoeken" : "Check Ambitieprofiel"}
              </h2>
              <p className="text-gray-500">
                {activeRevision?.section === "SKILLS"
                  ? "We gebruiken de bestaande ambitietekst om opnieuw vacatures te zoeken."
                  : "Pas de tekst aan waar nodig voordat we vacatures zoeken."}
              </p>
                </div>

            {activeRevision?.section !== "SKILLS" && (
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <TextArea 
                  rows={12}
                  value={ambitionText}
                  onChange={(e) => setAmbitionText(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            )}

                <div className="flex justify-end items-center gap-4">
                    {busy && (
                        <span className="text-sm text-gray-500 animate-pulse">
                            ⏳ We doorzoeken meerdere bronnen, dit kan even duren...
                        </span>
                    )}
                    <Button onClick={handleRunVacancySearch} disabled={busy}>
                {busy ? "Zoeken..." : activeRevision?.section === "SKILLS" ? "Vacatures zoeken" : "Opslaan & Vacatures Zoeken ➤"}
                    </Button>
                </div>
            </div>
        )}

        {/* STAP 5: VACATURE EDITOR */}
        {step === 5 && (
            <div className="animate-fade-in-up space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-extrabold text-gray-900">Vacature Check</h2>
                  <p className="text-gray-500 mt-2">
                    Dit hebben we gevonden. Pas aan of voeg toe voor de beste matching.
                  </p>
                </div>

                {noVacanciesFoundOnline && vacancies.length === 0 && (
                  <Card className="border-2 border-amber-300 bg-amber-50">
                    <div className="space-y-2 text-amber-900">
                      <p className="text-sm font-bold uppercase tracking-wide">Geen online vacatures gevonden</p>
                      <p className="text-sm leading-relaxed">
                        We hebben online geen vacatures kunnen vinden voor jullie organisatie.
                        Voeg daarom zelf 1 of 2 vacatures toe. Dan kan het platform toch relevante ESCO skills extraheren en een sterker skillsprofiel opbouwen voor matching.
                      </p>
                      <p className="text-sm font-semibold">Tip: begin met de belangrijkste functie(s) die je nu zou willen invullen.</p>
                    </div>
                  </Card>
                )}

                <div className="space-y-4">
                    {vacancies.map((vac, idx) => (
                        <Card key={idx} className="relative border-l-4 border-l-blue-500 shadow-md">
                            {/* Verwijder knop */}
                            <button 
                                onClick={() => removeVacancy(idx)}
                                className="absolute top-4 right-4 text-gray-300 hover:text-red-500 font-bold px-2"
                                title="Verwijder vacature"
                            >
                                ✕
                            </button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <Label>Functietitel</Label>
                                    <Input 
                                        value={vac.job_title} 
                                        onChange={e => handleVacancyChange(idx, "job_title", e.target.value)} 
                                        placeholder="Bijv. Senior Python Developer"
                                    />
                                </div>
                                <div>
                                    <Label>Locatie</Label>
                                    <Input 
                                        value={vac.location} 
                                        onChange={e => handleVacancyChange(idx, "location", e.target.value)} 
                                        placeholder="Bijv. Amsterdam / Remote"
                                    />
                                </div>
                            </div>
                            
                            <div className="mb-4">
                                <Label>Type Dienstverband</Label>
                                <Input 
                                    value={vac.employment_type} 
                                    onChange={e => handleVacancyChange(idx, "employment_type", e.target.value)} 
                                    placeholder="Bijv. Full-time, Freelance"
                                />
                            </div>

                            <div className="mb-4">
                                <Label>Vereiste Skills (gescheiden door komma's)</Label>
                                <Input 
                                    // Toon de _skillString als die er is (tijdens typen), anders join de array
                                    value={vac._skillString !== undefined ? vac._skillString : (vac.required_skills || []).join(", ")} 
                                    onChange={e => handleSkillChange(idx, e.target.value)} 
                                    placeholder="Python, Leiderschap, Communicatie..."
                                />
                            </div>

                            <div>
                                <Label>Korte Beschrijving</Label>
                                <textarea 
                                    className="min-h-[80px] w-full rounded-lg border-2 border-[#151827]/25 px-3 py-2 focus:border-[#3f7f65] focus:outline-none"
                                    rows={3}
                                    value={vac.job_description} 
                                    onChange={e => handleVacancyChange(idx, "job_description", e.target.value)} 
                                />
                            </div>
                        </Card>
                    ))}

                    {/* Toevoegen knop */}
                    <button 
                        onClick={addVacancy}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#151827]/30 py-4 font-bold text-gray-500 transition-colors hover:border-[#3f7f65] hover:text-[#3f7f65]"
                    >
                        <span className="text-xl">+</span> Nieuwe Vacature Toevoegen
                    </button>
                </div>

                <div className="pt-6 flex justify-end">
                    <Button onClick={handleSubmitVacancies} disabled={busy}>
                      {busy ? "Genereren..." : "Bevestig & Genereer Skills Profiel ➤"}
                    </Button>
                </div>
            </div>
        )}

        {/* STAP 6: VOORWAARDEN UPLOADEN */}
        {step === 6 && (
            <div className="animate-fade-in-up space-y-6">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900">Arbeidsvoorwaarden</h2>
                    <p className="text-gray-500 mt-2">
                        Kies hoe je de arbeidsvoorwaarden wilt doorgeven: via document upload of handmatig aanklikken.
                    </p>
                </div>

                <div className="mx-auto flex w-full max-w-xl rounded-xl border-2 border-[#151827]/20 bg-white p-1">
                    <button
                        type="button"
                        onClick={() => setConditionsMode("upload")}
                        className={`flex-1 rounded-lg px-4 py-2 text-sm font-bold transition-colors ${conditionsMode === "upload" ? "bg-[#3f7f65] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                        Document upload
                    </button>
                    <button
                        type="button"
                        onClick={() => setConditionsMode("manual")}
                        className={`flex-1 rounded-lg px-4 py-2 text-sm font-bold transition-colors ${conditionsMode === "manual" ? "bg-[#3f7f65] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                        Handmatig aanklikken
                    </button>
                </div>

                <Card className="flex min-h-[300px] flex-col items-center justify-center border-2 border-dashed border-[#151827]/30 p-12 text-center transition-colors hover:bg-white/70">
                    
                    {busy ? (
                        <div className="flex flex-col items-center">
                            <svg className="mb-4 h-10 w-10 animate-spin text-[#3f7f65]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {conditionsMode === "upload" ? (
                                <>
                                  <p className="text-gray-700 font-semibold">Bestand ontvangen. We verwerken nu je arbeidsvoorwaarden...</p>
                                  <p className="text-sm text-gray-600 mt-2 text-center max-w-xl leading-relaxed">
                                    Dit kan even duren: we lezen het document uit, laten AI de harde eisen en randvoorwaarden extraheren
                                    en bouwen daarna proefperiode-voorbeelden op basis van je profiel en vacatures.
                                  </p>
                                </>
                            ) : (
                                <>
                                  <p className="text-gray-700 font-semibold">Handmatige voorwaarden verwerken...</p>
                                  <p className="text-sm text-gray-600 mt-2 text-center max-w-xl leading-relaxed">
                                    We slaan je selectie op en verwerken deze direct voor matching en proefperiode-voorbeelden.
                                  </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {conditionsMode === "upload" ? (
                                <>
                                    <div className="mb-4 text-gray-300">
                                        {/* Icoon document upload */}
                                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    
                                    <label className="cursor-pointer rounded-full bg-[#3f7f65] px-8 py-3 font-bold text-white shadow-lg transition-all hover:bg-[#151827] hover:shadow-xl">
                                        <span>Kies Bestand (PDF/Word)</span>
                                        <input 
                                            type="file" 
                                            className="hidden" 
                                            accept=".pdf,.docx,.doc,.txt"
                                            onChange={handleConditionsUpload}
                                        />
                                    </label>
                                    <p className="text-xs text-gray-400 mt-4">Wordt direct verwerkt. Geen opslag van documentinhoud.</p>
                                </>
                            ) : (
                                <div className="w-full max-w-3xl text-left">
                                    <p className="mb-4 text-sm text-gray-600">
                                      Klik per arbeidsvoorwaarde aan of deze door jullie wordt aangeboden.
                                    </p>

                                    {loadingConstraintOptions ? (
                                      <p className="text-sm text-gray-500">Opties laden...</p>
                                    ) : (
                                      <div className="space-y-5">
                                        {Object.entries(
                                          constraintOptions.reduce((acc, opt) => {
                                            const category = opt.category || "Overig";
                                            if (!acc[category]) acc[category] = [];
                                            acc[category].push(opt);
                                            return acc;
                                          }, {})
                                        ).map(([category, options]) => (
                                          <div key={category} className="rounded-xl border border-[#151827]/20 bg-white p-4">
                                            <p className="mb-3 text-xs font-black uppercase tracking-[0.12em] text-[#151827]/65">{category}</p>
                                            <div className="space-y-3">
                                              {options.map((opt) => {
                                                const isOffered = manualConstraints[opt.key] === true;
                                                const isNotOffered = manualConstraints[opt.key] === false;
                                                const primaryText = opt.description || opt.label;

                                                return (
                                                  <div key={opt.key} className="rounded-lg border border-[#151827]/15 p-3">
                                                    <p className="text-sm text-[#151827] leading-relaxed">{primaryText}</p>
                                                    {opt.description && opt.label && (
                                                      <p className="mt-1 text-[11px] uppercase tracking-wide text-gray-400">{opt.label}</p>
                                                    )}
                                                    <div className="mt-3 flex gap-2">
                                                      <button
                                                        type="button"
                                                        onClick={() => setManualConstraintValue(opt.key, true)}
                                                        className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${isOffered ? "border-[#3f7f65] bg-[#3f7f65] text-white" : "border-gray-300 bg-white text-gray-600 hover:border-[#3f7f65]"}`}
                                                      >
                                                        Aangeboden
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => setManualConstraintValue(opt.key, false)}
                                                        className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${isNotOffered ? "border-[#151827] bg-[#151827] text-white" : "border-gray-300 bg-white text-gray-600 hover:border-[#151827]"}`}
                                                      >
                                                        Niet aangeboden
                                                      </button>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <div className="mt-6 flex justify-end">
                                      <Button onClick={handleSubmitManualConditions} disabled={loadingConstraintOptions || constraintOptions.length === 0}>
                                        Bevestig arbeidsvoorwaarden ➤
                                      </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card>
            </div>
        )}
        {/* STAP 7: FINAL & MATCHING */}
        {step === 7 && (
            <div className="animate-fade-in-up space-y-8">

                {/* Deel A: Succes Melding (fallback, primair via IntakeCompleteModal pop-up) */}
                {!showCompletionModal && (
                    <div className="text-center py-10">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-sm">
                            ✔
                        </div>
                        <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Intake Afgerond!</h2>
                        <p className="text-gray-600">
                            De intake is succesvol afgerond! Je profiel wordt nu opgeslagen. We komen bij je terug zodra we geschikte kandidaten hebben gevonden. Bedankt voor je tijd!
                        </p>
                        {onBackToDashboard && (
                            <div className="mt-6">
                                <button
                                    onClick={onBackToDashboard}
                                    className="transform rounded-full bg-[#3f7f65] px-8 py-3 font-bold text-white shadow-lg transition-all hover:scale-105 hover:bg-[#151827]"
                                >
                                    Terug naar Dashboard →
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Deel C: De Resultaten */}
                {opportunities && (
                    <div className="animate-fade-in-up">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Gevonden Kandidaten ({opportunities.length})</h3>
                        
                        <div className="space-y-4">
                            {opportunities.length === 0 && <p className="text-gray-500 italic">Geen matches gevonden.</p>}
                            
                            {opportunities.map((opp, idx) => (
                                <Card key={idx} className="border-l-4 border-l-[#3f7f65] transition-shadow hover:shadow-md">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="text-lg font-bold text-[#3f7f65]">{opp.candidate?.name || "Kandidaat " + (idx+1)}</h4>
                                            <p className="text-sm text-gray-600 mt-1">{opp.match_summary || "Match op basis van skills & cultuur."}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-[#713d8d]">{opp.total_score ? opp.total_score.toFixed(1) : "?"}/10</div>
                                            <div className="text-xs text-gray-400 uppercase font-bold">Match Score</div>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )}

      </main>

      {/* Pop-up bij volledige afronding van de intake */}
      {showCompletionModal && (
        <IntakeCompleteModal
          accentColor="#713d8d"
          title="Intake afgerond!"
          thankYouText="Bedankt voor de tijd en moeite die je in het invullen van de intake hebt gestoken."
          nextStepsTitle="Wat zijn de vervolgstappen?"
          nextStepsBody="De matching vindt elke nacht plaats. Nieuwe matches vind je terug in je dashboard bij Opportunities. Elke match wordt eerst beoordeeld door onze professionals. Bij een kansrijke match initieert WeRepresent het eerste contact en begeleiden we de vervolgstap."
          buttonLabel="Terug naar dashboard"
          onClose={() => {
            setShowCompletionModal(false);
            if (onBackToDashboard) onBackToDashboard();
          }}
        />
      )}
    </div>
  );
}